import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  ActiveWorkspaceUpdatedEvent,
  CachedWorkspaceProjectsUpdatedEvent,
  CachedWorkspacesUpdatedEvent,
  WorkspaceDTO,
} from '../interfaces/workspace.interface';
import { ApiRequestOptions, ApiService } from './api.service';
import { EmitterService } from './emitter.service';

@Injectable()
export class WorkspaceService {
  private activeWorkspace: WorkspaceDTO = null;
  private inMarketplace: boolean = false; //placeholder workspace for production company
  private availableWorkspaces: any = []; //cached workspaces
  private availableProjects: any = []; //cached projects
  private activeWorkspaceCover: string = '';
  private activeWorkspaceAvatar: string = '';
  workspaceDataEmitter = new Subject<void>();

  private readonly messageSource = new BehaviorSubject(false);
  currentStatus = this.messageSource.asObservable();
  formValue: any;
  private readonly currentWorkspace$ = new BehaviorSubject<WorkspaceDTO | null>(this.activeWorkspace);

  constructor(private readonly apiService: ApiService) {}

  getCurrentWorkspace(): Observable<WorkspaceDTO> {
    return this.currentWorkspace$.asObservable();
  }

  changeStatus(isClicked: boolean) {
    this.messageSource.next(isClicked);
  }
  refreshWorkspaceData() {
    this.workspaceDataEmitter.next();
  }
  get ActiveWorkspaceCover() {
    return this.activeWorkspaceCover;
  }

  get ActiveWorkspaceAvatar() {
    return this.activeWorkspaceAvatar;
  }

  getWorkspaceById(
    companyId: string,
    workspaceId: string,
    includeBrand: boolean = false,
    options: ApiRequestOptions = {}
  ): Observable<any> {
    const url = `/api/workspace/${companyId}/${workspaceId}?includeBrandId=${includeBrand}`;
    return this.apiService.httpGet(url, false, {}, options);
  }

  /** The workspace, or null when it cannot be read (403/404) so name lookups degrade to an empty label. */
  findWorkspaceById(companyId: string, workspaceId: string): Observable<WorkspaceDTO | null> {
    return this.getWorkspaceById(companyId, workspaceId).pipe(catchError(() => of(null)));
  }

  getProjectsInWorkspaces(companyId: string, workspaceId: string): Observable<any> {
    const url = `/api/workspace/${companyId}/workspace/${workspaceId}`;
    return this.apiService.httpGet(url, false, {});
  }

  createWorkspace(data: WorkspaceDTO, brandId: string) {
    let url = `/api/workspace/${data.companyId}`;
    const urlParams = [];
    if (brandId) {
      urlParams.push('brandId=' + encodeURIComponent(brandId));
    }
    const queryString = urlParams.join('&');
    if (queryString) {
      url += '?' + queryString;
    }
    return this.apiService.httpPost(url, data);
  }

  editWorkspace(workspaceId: string, data: WorkspaceDTO, brandId: string) {
    let url = `/api/workspace/${data.companyId}/${workspaceId}`;
    const urlParams = [];
    if (brandId) {
      urlParams.push('brandId=' + encodeURIComponent(brandId));
    }
    const queryString = urlParams.join('&');
    if (queryString) {
      url += '?' + queryString;
    }
    return this.apiService.httpPut(url, data);
  }

  /**
   * No-op: POST /api/workspace/{companyId}/{workspaceId}/addUser/{userName}/{roleType} is
   * retired (RBAC permission migration). Workspace membership is managed outside this app;
   * the stub keeps the calling UI flows intact.
   */
  addUserToWorkspace(_companyId: string, _workspaceId: string, _userName: string): Observable<any> {
    return of(null);
  }

  /**
   * No-op: POST /api/workspace/{companyId}/{workspaceId}/removeUser/{userName} is retired
   * (RBAC permission migration).
   */
  removeUserFromWorkspace(_companyId: string, _workspaceId: string, _userName: string): Observable<any> {
    return of(null);
  }

  deleteWorkspace(companyId: string, workspaceId: string) {
    const url = `/api/workspace/${companyId}/workspace/${workspaceId}`;
    return this.apiService.httpDelete(url);
  }

  deleteWorkspaceCover(companyId: string, workspaceId: string): Observable<any> {
    return this.apiService.httpDelete(`/api/workspace/${companyId}/${workspaceId}/cover`);
  }

  setActiveWorkspace(workspace: WorkspaceDTO) {
    this.activeWorkspace = { ...workspace };
    this.inMarketplace = false;
    this.currentWorkspace$.next(this.activeWorkspace);
    EmitterService.get(ActiveWorkspaceUpdatedEvent).emit({
      workspace,
      inMarketplace: false,
    });
  }

  unsetActiveWorkspace() {
    this.changeStatus(false);
    this.activeWorkspace = null;
    this.inMarketplace = false;
    this.activeWorkspaceAvatar = '';
    this.activeWorkspaceCover = '';
    this.currentWorkspace$.next(null);
    EmitterService.get(ActiveWorkspaceUpdatedEvent).emit({
      workspace: null,
      inMarketplace: this.inMarketplace,
    });
  }

  getActiveWorkspace(): WorkspaceDTO | null {
    if (!this.activeWorkspace) return null;
    return { ...this.activeWorkspace };
  }

  setInMarketplace(isIn: boolean) {
    this.inMarketplace = isIn;
    if (isIn) {
      this.activeWorkspace = null;
      this.activeWorkspaceAvatar = '';
      this.activeWorkspaceCover = '';
      this.currentWorkspace$.next(null);
    }
    EmitterService.get(ActiveWorkspaceUpdatedEvent).emit({
      workspace: this.activeWorkspace,
      inMarketplace: this.inMarketplace,
    });
  }

  getInMarketplace(): boolean {
    return this.inMarketplace;
  }

  setCachedWorkspaces(workspaces: any[]) {
    this.availableWorkspaces = workspaces;
    EmitterService.get(CachedWorkspacesUpdatedEvent).emit([...workspaces]);
  }

  getCachedWorkspaces() {
    return [...this.availableWorkspaces];
  }

  setCachedProjects(projects: any[]) {
    this.availableProjects = projects;
    EmitterService.get(CachedWorkspaceProjectsUpdatedEvent).emit([...projects]);
  }

  getCachedProjects() {
    return [...this.availableProjects];
  }

  clearWorkspaceCache() {
    this.availableWorkspaces = [];
    this.availableProjects = [];
  }

  //use for caching cover & avatar
  setActiveWorkspaceCoverAndAvatar(options) {
    if (!this.activeWorkspace) {
      return;
    }
    if (options.cover) {
      this.activeWorkspaceCover = options.cover;
    }
    if (options.avatar) {
      this.activeWorkspaceAvatar = options.avatar;
    }
  }

  removeActiveWorkspaceCover() {
    this.activeWorkspaceCover = '';
  }
}
